import React from 'react';
import { Dumbbell, Activity, Moon, Utensils, Pill, CheckSquare } from 'lucide-react';
import { EVENT_TYPES, EVENT_THEMES } from '@/shared/constants/event-types.v4';

interface EventTypeSelectorProps {
    currentType: string;
    onTypeChange: (type: string) => void;
}

/**
 * EventTypeSelector: Visual selection for the 'Citizen Type' of an event.
 * Uses horizontal icons with theme-specific colors.
 * SHADOW VERSION (.tsx)
 */
export const EventTypeSelector: React.FC<EventTypeSelectorProps> = ({ currentType, onTypeChange }) => {
    const types = [
        { id: EVENT_TYPES.PERFORMANCE, label: 'PERF', icon: Dumbbell },
        { id: EVENT_TYPES.MOBILITY, label: 'MOB', icon: Activity },
        { id: EVENT_TYPES.RECOVERY, label: 'RECOV', icon: Moon },
        { id: EVENT_TYPES.NUTRITION, label: 'NUTRI', icon: Utensils },
        { id: EVENT_TYPES.SUPPLEMENTATION, label: 'SUP', icon: Pill },
        { id: EVENT_TYPES.TASK, label: 'TASK', icon: CheckSquare }
    ];

    return (
        <div style={{ 
            display: 'flex', 
            backgroundColor: '#050505', 
            padding: '2px', 
            borderRadius: '10px', 
            border: '1px solid #1a1a1a', 
            gap: '2px',
            marginBottom: '15px',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            {types.map(t => {
                const isSelected = currentType === t.id;
                const themeColor = EVENT_THEMES[t.id];
                const Icon = t.icon;

                return (
                    <button
                        key={t.id}
                        onClick={() => onTypeChange(t.id)}
                        style={{
                            all: 'unset',
                            flex: 1,
                            minWidth: 0,
                            padding: '10px 0',
                            borderRadius: '8px',
                            backgroundColor: isSelected ? `${themeColor}1a` : 'transparent',
                            border: `1px solid ${isSelected ? themeColor : 'transparent'}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            // Unselected opacity higher for readability, but still distinct
                            opacity: isSelected ? 1 : 0.6
                        }}
                        aria-label={t.label}
                    >
                        {/* Use theme color even when unselected, but with lower opacity/saturation if needed. 
                            Here we use the actual theme color but the button opacity handles the 'dimming' */}
                        <Icon size={16} color={themeColor} style={{ opacity: isSelected ? 1 : 0.5 }} />
                        <span style={{ 
                            fontSize: '8px', 
                            fontWeight: '900', 
                            color: isSelected ? '#fff' : themeColor,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            opacity: isSelected ? 1 : 0.7
                        }}>
                            {t.label}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};
